import styled, { css } from "styled-components";
import React, { useEffect, useRef, useState } from "react";
// import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Modal from "../Modal/Modal";
import Tooltip from "../Tooltip/Tooltip";
import PopupHeader from "./components/PopupHeader";
import FailUploadAlert from "../FailUploadAlert/FailUploadAlert";
import useCustomAvatarPopupSteps from "../../hooks/useCustomAvatarPopupSteps";
import Button, { ButtonThemes } from "../Button/Button";
import { Video } from "../../types/video";
import { CustomAvatar } from "./SelectTypeCustomAvatarPopup";
import { Status } from "../../redux/actions/humansProjectActions";
import { getPrefilled } from "../../redux/reducers/popupsReducer";
import { uploadHumatarServer } from "../../redux/actions/humatarActions";
import { talkingImageTooltip, videoDetailsPoints } from "../../mocks/humans";
import { CircularProgress, InfoIcon, VideoIconOutlined } from "../Icons/Icons";
import { FullScreenPopupContentWrapper, FullScreenPopupTitle } from "./popups.styles";
import { getCustomHumatar, uploadingHumatarLoading } from "../../redux/reducers/humatarReducer";
// import Alert from "../../modules/AIHumans/components/Alert";
// import Textfield, { TextfieldVariant } from "../Textfield/Textfield";
// import { Popups, updatePopup } from "../../redux/actions/popupsActions";

const MAX_VIDEO_SIZE = 750 * 1024 * 1024; // 750 MB
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_IMAGE_RESOLUTION = 1024;

interface Props {
  open: boolean;
  onClose: () => void;
}

interface SubmitConfirmationPopupProps extends Props {
  isLoading: boolean;
  isCancelButtonDisabled: boolean;
  isSubmitButtonDisabled: boolean;
  handleSubmit: () => void;
}
const SubmitConfirmationPopup = ({
  open,
  onClose,
  isLoading,
  isCancelButtonDisabled,
  isSubmitButtonDisabled,
  handleSubmit,
}: SubmitConfirmationPopupProps) => {
  return (
    <Modal
      className="uploadCustomAvatarConfirmation"
      maxWidth={523}
      maxHeight={264}
      open={open}
      onClose={onClose}
      closeIcon={false}
    >
      <ConfirmationPopupWrapper>
        <ConfirmationTitle>Ready to Submit Your Avatar?</ConfirmationTitle>
        <ConfirmationDescription>
          Please review your video before submitting. Once submitted, you won't be able to make any changes.
        </ConfirmationDescription>
        <ButtonsWrapper>
          <Button disabled={isCancelButtonDisabled} text="Cancel" onClick={onClose} buttonTheme={ButtonThemes.White} />
          <Button
            disabled={isSubmitButtonDisabled}
            text={isLoading ? <CircularProgress /> : "Submit"}
            onClick={handleSubmit}
          />
        </ButtonsWrapper>
      </ConfirmationPopupWrapper>
    </Modal>
  );
};

const AddCustomAvatarPopup = ({ open, onClose }: Props) => {
  const { goToFirstStep, openInstantImageGuidePopup } = useCustomAvatarPopupSteps(onClose);

  const dispatch = useDispatch();
  const isFirstRender = useRef(true);
  const { noRefresh, type } = useSelector(getPrefilled);
  const customHumatar = useSelector(getCustomHumatar);

  const [title, setTitle] = useState("");
  const [uploadedFile, setUploadedFile] = useState<Video | null>(null);
  const [secondUploadedFile, setSecondUploadedFile] = useState<Video | null>(null); // for second training video
  const [isSecondVideo, setIsSecondVideo] = useState<boolean>(false); // for second training video
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
  const [checkBox, setCheckBox] = useState<boolean>(false);
  const [errorSizeSample, setErrorSizeSample] = useState<boolean>(false);
  const [errorTypeSample, setErrorTypeSample] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadHumatarLoading = useSelector(uploadingHumatarLoading);
  const isInstantAvatarUploader = type === CustomAvatar.InstantAvatar;

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const handleCloseConfirmationPopup = () => setOpenConfirmationPopup(false);

  const sampleUpload = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setErrorTypeSample(false);
    setErrorSizeSample(false);
    const file = target.files && target.files[0];

    if (!file) {
      return;
    }

    const isVideo = file.type === "video/mp4";
    const isImage = file.type === "image/jpeg" || file.type === "image/png";

    if (isVideo || isImage) {
      if (file.size > (isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE)) {
        setErrorSizeSample(true);
        return;
      }

      if (isImage) {
        const imageSize = await getImageSizeAsync(file);
        if (imageSize.width > MAX_IMAGE_RESOLUTION || imageSize.height > MAX_IMAGE_RESOLUTION) {
          setErrorSizeSample(true);
          return;
        }
      }

      setErrorSizeSample(false);

      const fileObj = {
        id: Math.random(),
        name: file.name,
        video: isVideo ? URL.createObjectURL(file) : null,
        image: isImage ? URL.createObjectURL(file) : null,
        file,
      };

      if (isSecondVideo) {
        setSecondUploadedFile(fileObj);
      } else {
        setUploadedFile(fileObj);
      }
    } else {
      setErrorTypeSample(true);
    }
  };

  const getImageSizeAsync = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleAddCustomAvatar =
    (action: (formData: FormData, filenames: string[], noRefresh?: boolean) => any) => () => {
      if (!uploadedFile) return;

      const formData = new FormData();
      const filenames = [uploadedFile.file?.name || ""];
      formData.append("File", uploadedFile?.file as File);
      formData.append("name", title);

      dispatch(action(formData, filenames, noRefresh));
    };

  const handleUploadInstantAvatar =
    (action: (formData: FormData, filenames: string[], noRefresh?: boolean) => any) => () => {
      // in-progress
      // if (!uploadedFile || !secondUploadedFile) return;
      // const formData = new FormData();
      // const filenames: string[] = [];
      // const file1 = uploadedFile?.file as File;
      // const file1Name = uploadedFile.file?.name || "";
      // const file2 = secondUploadedFile?.file as File;
      // const file2Name = secondUploadedFile.file?.name || "";
      // formData.append("File1", file1);
      // formData.append("File2", file2);
      // filenames.push(file1Name);
      // filenames.push(file2Name);
      // formData.append("name", title);
      // dispatch(action(formData, filenames, noRefresh));
    };

  const handleClose = () => {
    if (uploadHumatarLoading) return;

    onClose();
  };

  useEffect(() => {
    if (!isFirstRender.current && !uploadHumatarLoading && !customHumatar.isError) {
      onClose();
    }

    isFirstRender.current = false;
  }, [uploadHumatarLoading]);

  const handleStepBack = () => {
    if (isInstantAvatarUploader) {
      if (isSecondVideo) {
        setIsSecondVideo(false);
      } else {
        openInstantImageGuidePopup();
      }
    } else {
      goToFirstStep();
    }
  };
  const handleStepNext = () => {
    if (uploadedFile && !isSecondVideo) {
      setIsSecondVideo(true);
    } else if (uploadedFile && secondUploadedFile && isSecondVideo) {
      setOpenConfirmationPopup(true);
    } else {
      return;
    }
  };

  const allFields = {
    [CustomAvatar.InstantAvatar]: {
      icon: <VideoIconOutlined />,
      headerTitle: `Upload a ${!isSecondVideo ? "consent" : "training"} video`,
      isVideoUploader: true,
      dragAndDropTitle: <>Click to upload your {!isSecondVideo ? "consent" : "training"} video or drag and drop</>,
      dragAndDropDescription: "Currently, the H.264 codec is required to ensure efficient compression.",
      dragAndDropDescriptionContent: [
        "Video file up to 750mb",
        "Supported formats: .mp4 .webm",
        "Currently, the H.264 codec is required to ensure efficient compression.",
      ],
      isConfirmationField: false,
      acceptedFormats: "video/mp4",
      action: uploadHumatarServer,
      infoText: "",
      // infoText: (
      //   <Link to="/files/instructions.pdf" target="_blank">
      //     Shooting Instant Avatars Footage - Instructions
      //   </Link>
      // ),
    },
    [CustomAvatar.TalkingImage]: {
      icon: <img src="/images/ai-humans/talking-image.svg" />,
      headerTitle: "Upload your image",
      isVideoUploader: false,
      dragAndDropTitle: <>Drag and drop your own image, or browse image</>,
      dragAndDropDescriptionContent: [
        "Supported file format: .JPG or .PNG",
        "Maximum Size: Images - 5 MB. Maximum resolution: 1024x1024",
      ],
      dragAndDropFileSize: "",
      isConfirmationField: true,
      acceptedFormats: "image/jpeg, image/png",
      action: uploadHumatarServer,
      infoText: (
        <ItemWrapper>
          <span>Recommended photo requirements</span>
          <InfoIcon />
          <Tooltip
            text={
              <ol>
                {talkingImageTooltip.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ol>
            }
            position="top-left"
            reverseColor
          />
        </ItemWrapper>
      ),
    },
  };

  const {
    headerTitle,
    isVideoUploader,
    dragAndDropTitle,
    dragAndDropDescriptionContent,
    isConfirmationField,
    acceptedFormats,
    action,
    infoText,
    icon,
  } = allFields[type as CustomAvatar];

  return (
    <Wrapper>
      <Modal className="uploadCustomAvatar" open={open} onClose={handleClose}>
        <PopupHeader
          menuButtonAction={handleClose}
          handlePreviousButton={handleStepBack}
          handleNextButton={handleStepNext}
        />
        <Content isLarge={isInstantAvatarUploader}>
          <FullScreenPopupTitle>{headerTitle}</FullScreenPopupTitle>
          <MainContent>
            {!uploadedFile?.image &&
            ((!isSecondVideo && !uploadedFile?.video) || (isSecondVideo && !secondUploadedFile?.video)) ? (
              <DragAndDropOuterWrapper>
                <DragAndDropWrapper isVideoUploader={isVideoUploader}>
                  <input
                    type="file"
                    title=""
                    ref={inputRef}
                    multiple
                    accept={acceptedFormats}
                    onChange={sampleUpload}
                  />
                  {icon}
                  <h3>{dragAndDropTitle}</h3>
                  <SampleFormatWrapper>
                    {dragAndDropDescriptionContent?.map((item: string, index: number) => (
                      <SampleFormat
                        key={item}
                        isError={(index === 0 && errorTypeSample) || (index === 1 && errorSizeSample)}
                      >
                        {item}
                      </SampleFormat>
                    ))}
                  </SampleFormatWrapper>
                </DragAndDropWrapper>
                <InformationWrapper>{infoText}</InformationWrapper>
              </DragAndDropOuterWrapper>
            ) : (
              <>
                {!isSecondVideo && uploadedFile?.video && (
                  <video key={uploadedFile.video} width="100%" height="auto" controls>
                    <source src={uploadedFile.video} type="video/mp4" />
                  </video>
                )}
                {isSecondVideo && secondUploadedFile?.video && (
                  <LastStepWrapper>
                    <video key={secondUploadedFile.video} width="100%" height="auto" controls>
                      <source src={secondUploadedFile.video} type="video/mp4" />
                    </video>
                    <VideoDetailsHeader>
                      For an optimal, more realistic avatar, please confirm your video meets these requirements:
                    </VideoDetailsHeader>
                    <VideoDetailsWrapper>
                      {videoDetailsPoints.map((text) => (
                        <CheckboxWrapper key={text}>
                          <input type="checkbox" />
                          <span>{text}</span>
                        </CheckboxWrapper>
                      ))}
                    </VideoDetailsWrapper>
                  </LastStepWrapper>
                )}
                {uploadedFile?.image && (
                  <img
                    src={uploadedFile.image}
                    alt="Preview"
                    style={{ width: "100%", height: "auto", objectFit: "contain" }}
                  />
                )}
              </>
            )}
          </MainContent>
          {/* <InformationWrapper>{infoText}</InformationWrapper> */}
          {isConfirmationField && (
            <ConfirmWrapper>
              <input type="checkbox" checked={checkBox} onChange={() => setCheckBox(!checkBox)} required />
              <span>
                I confirm that I have all rights and consent to upload the video footage, and that the
                platform-generated content will not be used for illegal, fraudulent, or harmful purposes. As a customer,
                I agree to abide by Synthesys's{" "}
                <a href="https://synthesys.io/ai-privacy-policy/" target="_blank" rel="noreferrer">
                  Privacy policy
                </a>{" "}
                and{" "}
                <a href="https://synthesys.io/ai-terms/" target="_blank" rel="noreferrer">
                  Terms of services
                </a>
              </span>
            </ConfirmWrapper>
          )}
          {isInstantAvatarUploader ? (
            uploadedFile?.video ? (
              <ActionsWrapper>
                <Button className="upload-again-btn" text="Upload again">
                  <input type="file" title="" ref={inputRef} accept={acceptedFormats} onChange={sampleUpload} />
                </Button>
                <Button
                  text={!uploadedFile?.video || !secondUploadedFile?.video ? "Next" : "My footage looks good"}
                  onClick={handleStepNext}
                />
              </ActionsWrapper>
            ) : null
          ) : (
            <ButtonsWrapper>
              <Button
                disabled={uploadHumatarLoading}
                text="Cancel"
                onClick={handleClose}
                buttonTheme={ButtonThemes.White}
              />
              <Button
                disabled={uploadHumatarLoading || (isConfirmationField && !checkBox) || uploadedFile == null}
                text={uploadHumatarLoading ? <CircularProgress /> : "Upload"}
                onClick={handleAddCustomAvatar(action)}
              />
            </ButtonsWrapper>
          )}
        </Content>
      </Modal>
      {uploadedFile && secondUploadedFile && openConfirmationPopup && (
        <SubmitConfirmationPopup
          open={openConfirmationPopup}
          onClose={handleCloseConfirmationPopup}
          isLoading={uploadHumatarLoading}
          isCancelButtonDisabled={uploadHumatarLoading}
          // isSubmitButtonDisabled={uploadHumatarLoading}
          isSubmitButtonDisabled={true} // in-progress
          handleSubmit={handleUploadInstantAvatar(action)}
        />
      )}

      {customHumatar.isError && (
        <AlertWrapper>
          <FailUploadAlert status={Status.isError} text={customHumatar.errorMessage} />
        </AlertWrapper>
      )}
    </Wrapper>
  );
};
export const Wrapper = styled.div`
  /* .modal-wrapper {
    max-height: 100vh;
    max-width: 100%;
  } */
`;
export const AlertWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  max-width: 90%;
  z-index: 9999;
`;

const Content = styled(FullScreenPopupContentWrapper)<{ isLarge?: boolean }>`
  max-width: ${({ isLarge = false }) => (isLarge ? "660px" : "580px")};

  margin-inline: auto;
  padding: 20px 0px 0px 0px;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;

  video {
    /* max-height: 300px;
    max-width: 578px; */
    height: 340px;
    max-height: 340px;
    max-width: 610px;
    /* object-fit: contain; */
    object-fit: cover;
  }

  img {
    max-height: 300px;
    max-width: 578px;
  }
`;

const DragAndDropOuterWrapper = styled.div`
  width: 100%;
`;
const DragAndDropWrapper = styled.div<{ isVideoUploader?: boolean }>`
  position: relative;
  background: #141313;
  border: 1px dashed ${({ theme }) => theme.blueDark};
  border-radius: 20px;
  width: 100%;
  ${({ isVideoUploader = false }) =>
    isVideoUploader
      ? css`
          height: 210px;
          gap: 8px;
          @media (min-height: 525px) {
            /* height: 339px; */
            height: 341.6px;
            gap: 16px;
          }
        `
      : css`
          height: 187px;
          gap: 8px;
        `};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  min-height: 115px;

  & > h3 {
    font-family: "Mulish";
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: ${({ theme }) => theme.white};
    margin-top: 4px;
    margin-bottom: 8px;
    text-align: center;

    & > span {
      background: linear-gradient(142.13deg, #0063b4 16.78%, #009af7 85.53%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  & > input {
    width: 100%;
    height: 100%;
    opacity: 0;
    top: 0;
    left: 0;
    position: absolute;
    cursor: pointer;
  }

  @media (max-height: 775px) {
    /* height: 100px; */
    /* gap: 4px; */

    /* & > svg {
      width: 24px;
      height: 24px;
    } */

    & > h3 {
      font-size: 14px;
      margin-top: 0;
    }
  }

  @media (max-width: 500px) {
    /* height: 130px; */
    gap: 4px;

    & > svg {
      width: 24px;
      height: 24px;
    }

    & > h3 {
      font-size: 12px;
      margin-top: 0;
    }
  }
`;
const SampleFormatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SampleFormat = styled.p<{ isError?: boolean }>`
  font-family: "Mulish";
  font-weight: 400;
  font-size: 12px;
  line-height: 155%;
  color: ${({ theme }) => theme.white};
  text-align: center;

  ${({ isError }) =>
    isError &&
    `
       color: #ff5252;
  `}

  @media (max-width: 500px) {
    font-size: 10px;
  }
`;
export const LastStepWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
export const TextSmall = styled.div`
  font-size: 14px;
  font-family: 500;
  color: ${({ theme }) => theme.white};
`;
export const VideoDetailsHeader = styled(TextSmall)`
  margin-top: -8px;
`;
export const VideoDetailsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 20px;
  background-color: #121212; /* Dark background */
  border-radius: 10px;
  width: 100%;
  max-width: 660px;
`;
export const CheckboxWrapper = styled(TextSmall)`
  display: flex;
  align-items: center;
  gap: 12px;
  & > input {
    height: 15px;
    width: 15px;
    &:disabled {
      cursor: not-allowed;
      background-color: #fff;
      border-color: #fff;
      opacity: 1;
    }
  }
`;

const InformationWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 0px 0px 8px;

  svg {
    flex-shrink: 0;
  }

  a {
    color: ${({ theme }) => theme.white};
    text-decoration: underline;
    font-size: 14px;
  }

  @media (max-width: 520px) and (max-height: 775px) {
    span {
      font-size: 8px;
    }
  }
`;

const ConfirmWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  a {
    color: ${({ theme }) => theme.white};
    text-decoration: underline;
  }

  input {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  input[type="checkbox"] {
    background-color: transparent;
  }

  span {
    font-family: "Mulish";
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 155%;
    color: ${({ theme }) => theme.white};
    opacity: 0.6;
  }

  @media (max-width: 520px) and (max-height: 775px) {
    span {
      font-size: 8px;
    }
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 18px;
  gap: 24px;

  & button {
    box-shadow: none;
    padding-inline: 12px;
    min-width: 130px;
    width: fit-content;
    height: 34px;
    border-radius: 5px;
    :disabled {
      cursor: not-allowed;
    }

    & > span {
      font-size: 14px;
      line-height: 18px;
      font-family: 600;
    }
  }
  .upload-again-btn {
    background: #6b69702e;
    border: none;
    position: relative;
    overflow: hidden;
    & input {
      opacity: 0;
      position: absolute;
      inset: 0;
      cursor: pointer;
    }
  }

  @media (min-width: 950px) and (max-height: 720px) {
    height: auto;
    margin-top: 8px;
    gap: 36px;

    button {
      max-height: 48px;
    }
  }
`;

const SoundtrackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0;
  overflow: hidden auto;
  max-height: 200px;

  ::-webkit-scrollbar {
    display: none;
  }

  @media (max-height: 650px) {
    max-height: 120px;
  }

  @media (max-width: 520px) and (max-height: 775px) {
    max-height: 120px;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  position: relative;
  gap: 4px;
  align-items: center;
  cursor: pointer;

  span {
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: center;
    color: #ffffff;
  }

  svg {
    transform: rotate(180deg);
    width: 14px;
    height: 14px;
    path {
      stroke: #fff;
    }
  }

  &:hover > div {
    opacity: 1;
    visibility: visible;
    box-shadow: none;
    border-radius: 4px;
    padding: 4px 8px 6px !important;
    width: 250%;

    & > span {
      display: flex;
      flex-direction: column;
      gap: 12px;

      & > ol {
        margin-inline-start: 12px;
        margin-block-start: 4px;
        margin-block-end: 4px;

        & > li {
          list-style-type: decimal !important;
          margin-bottom: 12px;
          font-size: 11px !important;
          line-height: 14px !important;
          text-align: left;
          text-transform: none;

          &:last-of-type {
            margin-bottom: 0;
          }
        }
      }

      & > span,
      li::marker {
        font-size: 11px !important;
        line-height: 14px !important;
        text-align: left;
        text-transform: none;
      }
    }
  }

  @media (max-width: 900px) {
    & > div {
      display: none;
    }
  }

  @media (max-width: 700px) {
    span {
      font-size: 10px;
    }
  }
`;

export const ConfirmationPopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
export const ConfirmationTitle = styled(FullScreenPopupTitle)`
  text-align: start;
`;
export const ConfirmationDescription = styled(SampleFormat)`
  text-align: start;
`;
const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 24px;
  & button {
    box-shadow: none;
    :disabled {
      cursor: not-allowed;
    }
  }
`;

export default AddCustomAvatarPopup;
